/**
 * wishlistSearchString — turn a trainer's wish list into ONE Pokémon GO search
 * string you can paste into the in-game search bar to surface every species
 * they are chasing at once.
 *
 * Shape (same convention as tradeString.ts's builder):
 *   <dex numbers, comma-separated = OR> & !traded
 * e.g. a seven-slot list becomes
 *   `25,455,578,34,37,28&!traded`
 *
 * We search by DEX NUMBER (not name) so it is exact and form/spelling-proof,
 * and drop the wished traits entirely: the point is "which of these species can
 * I still trade them?", so the only filter is `!traded` — it hides copies you
 * already received in a trade (a Pokémon can only be traded once), leaving the
 * ones you can actually send. Duplicate numbers (a species wished in two
 * variants) collapse to one.
 */
import type { WishlistEntry } from "@/lib/wishlistStore";

/**
 * Build the search string for a wish list: the unique dex numbers in list
 * order, joined with commas, AND-chained with `!traded`. Returns "" for an
 * empty list.
 */
export function buildWishlistSearchString(entries: WishlistEntry[]): string {
  const seen = new Set<number>();
  const numbers: number[] = [];
  for (const entry of entries) {
    if (!Number.isFinite(entry.dexNumber) || seen.has(entry.dexNumber)) continue;
    seen.add(entry.dexNumber);
    numbers.push(entry.dexNumber);
  }
  if (numbers.length === 0) return "";
  return `${numbers.join(",")}&!traded`;
}
